import { getCookie } from '@/helpers/cookieMetods';
import { apiUrl, domain, parseJsonOrThrow } from './http';

export interface MerchReportRow {
  user_id: string;
  user_code: string;
  user_name: string;
  user_email: string;
  total_sc: string;
  purchased_merch_count: string;
}

export interface UserMerchItem {
  title: string;
  order: string;
  adddate?: string;
  variant: string | null;
  price?: string | number;
  how?: 'buy' | 'gift';
}

/** Row from `merch_list.php` — one merch item with its buyers. */
export interface MerchBuyersRow {
  merch_id: string | number;
  merch_title: string;
  total_buyers: number;
  /** Buyer names joined with `, `. */
  employees: string;
}

export interface MerchBuyersArgs {
  /** Current admin's user code (Graph id). */
  usercode: string;
  /** Order date range, `YYYY-MM-DD`; omitted = the whole period. */
  date_from?: string;
  date_to?: string;
}

export interface MerchBuyersReport {
  rows: MerchBuyersRow[];
  /** Absolute URL of the CSV export for the same filter, if generated. */
  exportUrl: string | null;
}

interface MerchBuyersResponse {
  success?: boolean;
  /** The PHP answers `{ ststus: 'error' }` (sic) on bad auth. */
  ststus?: string;
  status?: string;
  export_url?: string | null;
  data?: MerchBuyersRow[] | null;
}

/**
 * POST JSON — merch items from `completed` orders with their buyers, plus a
 * CSV export link. `success: false` just means nothing was found.
 */
export async function fetchMerchBuyers({
  usercode,
  date_from,
  date_to,
}: MerchBuyersArgs): Promise<MerchBuyersReport> {
  const token = getCookie('_form_token');
  const response = await fetch(apiUrl('/php/merch_list.php'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      usercode,
      token,
      ...(date_from ? { date_from } : {}),
      ...(date_to ? { date_to } : {}),
    }),
  });
  const body = await parseJsonOrThrow<MerchBuyersResponse>(response);
  if (body.ststus === 'error' || body.status === 'error') {
    throw new Error('Не вдалося завантажити звіт по мерчу');
  }
  const exportUrl = body.export_url
    ? /^https?:\/\//.test(body.export_url)
      ? body.export_url
      : `${domain}/${body.export_url.replace(/^\/+/, '')}`
    : null;
  return { rows: body.data ?? [], exportUrl };
}

/** Order date range, `YYYY-MM-DD`; omitted = the whole period. */
export interface MerchDateRange {
  date_from?: string;
  date_to?: string;
}

export async function fetchMerchTotalReport({
  date_from,
  date_to,
}: MerchDateRange = {}): Promise<MerchReportRow[]> {
  const token = getCookie('_form_token');
  const response = await fetch(apiUrl('/php/merch_total_report.php'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      token,
      ...(date_from ? { date_from } : {}),
      ...(date_to ? { date_to } : {}),
    }),
  });
  return parseJsonOrThrow<MerchReportRow[]>(response);
}

export async function fetchUserMerchItems(
  userCode: string
): Promise<UserMerchItem[]> {
  const token = getCookie('_form_token');
  const response = await fetch(apiUrl('/php/merch.php'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ usercode: userCode, token }),
  });
  const body = await parseJsonOrThrow<unknown>(response);
  if (Array.isArray(body)) return body as UserMerchItem[];
  if (body && typeof body === 'object' && 'data' in body) {
    const raw = body as { data: unknown };
    return Array.isArray(raw.data) ? (raw.data as UserMerchItem[]) : [];
  }
  return [];
}
